import React, { useEffect, useRef, useState } from "react";
import { Form, Input, InputNumber } from "antd";

const { TextArea } = Input;

/** 文本字段空值归一：null / undefined / "" 一律视为 ""，用于比较与发送，避免重复 PATCH。 */
const normText = (value) => ((value ?? "") === "" ? "" : String(value));
/** 数字字段空值归一：null / undefined / "" 一律视为 null。 */
const normNumber = (value) => (value === undefined || value === null || value === "" ? null : value);

/**
 * 表头要人填的项：RCO / 停机位 / 机组人数 / 备注（ATD/ATA 改到顶部航班信息行就地填）。
 * 即时保存：每个字段在 onBlur 写回，只在值真正变化时才发请求；机组人数只接受非负整数。
 *
 * onBlur 直接读 state 会拿到上一轮 render 的旧值（"改完立刻失焦"时可能漏存最后一笔），
 * 因此用 draftRef 镜像最新值，onBlur 一律读 ref。
 */
export default function ChecklistHeaderForm({ header, readOnly = false, onPatch }) {
    const attributes = header?.attributes || {};
    const [rco, setRco] = useState("");
    const [parkingSpot, setParkingSpot] = useState("");
    const [crewFigure, setCrewFigure] = useState(null);
    const [remark, setRemark] = useState("");

    const draftRef = useRef({ rco: "", parking_spot: "", crew_figure: null, remark: "" });

    // 依赖里带上 header.item_id：切到另一张单时，即使四个字段值恰好相同也要重置本地草稿。
    useEffect(() => {
        const next = {
            rco: attributes.rco || "",
            parking_spot: attributes.parking_spot || "",
            crew_figure: attributes.crew_figure ?? null,
            remark: attributes.remark || "",
        };
        setRco(next.rco);
        setParkingSpot(next.parking_spot);
        setCrewFigure(next.crew_figure);
        setRemark(next.remark);
        draftRef.current = next;
    }, [header?.item_id, attributes.rco, attributes.parking_spot, attributes.crew_figure, attributes.remark]);

    const changeField = (field, value, setter) => {
        draftRef.current = { ...draftRef.current, [field]: value };
        setter(value);
    };

    const saveField = (field, normalize) => {
        if (!header || readOnly) return;
        const current = normalize(draftRef.current[field]);
        const previous = normalize(attributes[field]);
        if (current === previous) return;
        onPatch(header.item_id, { [field]: current }, header.lock_version);
    };

    return (
        <Form layout="vertical">
            <Form.Item label="RCO" style={{ marginBottom: 12 }}>
                <Input
                    value={rco}
                    disabled={readOnly}
                    onChange={(e) => changeField("rco", e.target.value, setRco)}
                    onBlur={() => saveField("rco", normText)}
                    placeholder="RCO"
                />
            </Form.Item>
            <Form.Item label="Parking Spot" style={{ marginBottom: 12 }}>
                <Input
                    value={parkingSpot}
                    disabled={readOnly}
                    onChange={(e) => changeField("parking_spot", e.target.value, setParkingSpot)}
                    onBlur={() => saveField("parking_spot", normText)}
                    placeholder="Parking Spot"
                />
            </Form.Item>
            <Form.Item label="Crew Figure" style={{ marginBottom: 12 }}>
                <InputNumber
                    value={crewFigure}
                    disabled={readOnly}
                    min={0}
                    precision={0}
                    style={{ width: "100%" }}
                    onChange={(value) => changeField("crew_figure", value, setCrewFigure)}
                    onBlur={() => saveField("crew_figure", normNumber)}
                    placeholder="Number"
                />
            </Form.Item>
            <Form.Item label="Remark" style={{ marginBottom: 0 }}>
                <TextArea
                    value={remark}
                    disabled={readOnly}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    onChange={(e) => changeField("remark", e.target.value, setRemark)}
                    onBlur={() => saveField("remark", normText)}
                    placeholder="Remark"
                />
            </Form.Item>
        </Form>
    );
}
